import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui';
import APIClient from '../api/api';

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Missing reset token.');
      return;
    }
    if (password.length < 10) {
      setError('Password must be at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await APIClient.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Reset link is invalid or has expired.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="w-full max-w-[340px] rounded-[14px] border border-border bg-panel px-[26px] pb-6 pt-[26px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)]">
        <div className="flex flex-col items-center gap-3 mb-5">
          <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[13px] bg-gradient-to-br from-[#5b5bd6] to-[#8b5cf6] text-[22px] font-bold text-white shadow-[0_8px_24px_-8px_rgba(91,91,214,0.55)]">
            P
          </div>
          <div className="text-[14px] font-semibold tracking-tight text-text">Parity</div>
        </div>

        {done ? (
          <>
            <h1 className="mb-1 text-center text-[16px] font-bold tracking-tight text-text">Password updated</h1>
            <div className="mb-2 text-center text-[12px] text-text-3">
              Redirecting you to sign in…
            </div>
          </>
        ) : (
          <>
            <h1 className="mb-1 text-center text-[16px] font-bold tracking-tight text-text">Set a new password</h1>
            <div className="mb-5 text-center text-[12px] text-text-3">
              Choose a password of at least 10 characters.
            </div>

            <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-text-3">New password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-[13px] text-text outline-none focus:border-border-strong"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-text-3">Confirm password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-[13px] text-text outline-none focus:border-border-strong"
                />
              </label>
              {error && (
                <div className="rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1.5 text-[12px] text-danger">
                  {error}
                </div>
              )}
              <Button type="submit" variant="primary" loading={submitting} className="mt-2 w-full justify-center">
                Set password
              </Button>
              <div className="text-center text-[11px] text-text-3">
                <Link to="/login" className="hover:text-text-2">Back to sign in</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
